import React, { useCallback, useState } from 'react';

import Collapsible from '../../../../library/collapsible/collapsible';
import Image from '../../../../library/image/image';
import Typography from '../../../../library/typography/typography';
import FitmentBanner from '../../../../components/fitment-banner/fitment-banner';
import TreadwellPersonaIcon from '../../../../components/treadwell-persona-icon/treadwell-persona-icon';
import VehicleImage from '../../../../components/vehicle-image/vehicle-image';
import ProductListProfileChip from './components/product-list-profile-chip/product-list-profile-chip';

import {
  useFeatureFlags,
  useFitment,
  useModal,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';

import { FEATURE_FLAGS } from '../../../../shared/constants/feature-flags';
import { SHOP_TIRES_FLOW_MODAL } from '../../../../shared/constants/modals';

import './product-list-profile-chips.scss';

interface ProductListProfileChipsProps {
  showTreadwellProfile: boolean;
}

function ProductListProfileChips(props: ProductListProfileChipsProps) {
  const { showTreadwellProfile } = props;
  const [isCollapsible, setIsCollapsible] = useState(false);
  const { partialFitment, selectedFitment } = useFitment();
  const { openModal } = useModal();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { featureFlags } = useFeatureFlags();
  const isTreadwellFitmentStepsReorder = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_TREADWELL_FITMENT_STEPS_REORDER__]
  );

  const { selectedDriverType, zipCodeWeatherData } = treadwellPreferences || {};
  const { icon, name } = selectedDriverType || {};
  const { city, state } = zipCodeWeatherData || {};

  const fitment = selectedFitment || partialFitment;
  const { make, model, smallImageUrl, trim, year } = fitment || {};

  const defaultEditFitment = useCallback(() => {
    window.location.hash = '#/fitment/vehicle/edit/confirm';
  }, []);

  return (
    <div className="product-list-profile-chips" styleName="container">
      {fitment && (
        <Collapsible
          isOpen={isCollapsible}
          label={
            isCollapsible ? (
              <FitmentBanner
                fitment={fitment}
                isPartialFitment={!selectedFitment && partialFitment}
                onEditFitment={() => {
                  defaultEditFitment();
                  trackAction('vehicle_page_edit');
                }}
                showChangeVehicleLink
                showEditLink
                styleName="fitment-banner"
              />
            ) : (
              <div styleName="banner-container">
                <VehicleImage
                  alt="vehicle"
                  height="31"
                  smallImageUrl={smallImageUrl}
                  styleName="vehicle-image"
                  width="70"
                />
                <div styleName="vehicle-description">
                  <strong>
                    {year} {make}
                  </strong>{' '}
                  {model} {trim}
                </div>
              </div>
            )
          }
          onToggle={() => setIsCollapsible(!isCollapsible)}
          toggleType="CHEVRON"
        />
      )}

      {showTreadwellProfile && (
        <ProductListProfileChip
          onClick={() =>
            openModal(SHOP_TIRES_FLOW_MODAL, {
              hideZipBackButton: !isTreadwellFitmentStepsReorder,
              shouldStartAtTreadwell: true
            })
          }
        >
          <Image
            alt="Treadwell"
            height="24px"
            src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
            styleName="treadwell-icon"
          />

          <Typography tag="span" weight="heavy">
            {name && (
              <TreadwellPersonaIcon iconName={icon} styleName="persona-icon" />
            )}{' '}
            {name || 'Custom'}
          </Typography>

          <Typography tag="span">
            <span styleName="city">{city?.toLowerCase()}</span>, {state}
          </Typography>
        </ProductListProfileChip>
      )}
    </div>
  );
}

export default ProductListProfileChips;
